import { useState, useRef, useCallback, memo } from 'react';
import { GripVertical, Plus, X } from 'lucide-react';

interface Props {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  minItems?: number;
  placeholder?: string;
}

export const EditableList = memo(function EditableList({
  label,
  items,
  onChange,
  minItems = 1,
  placeholder = 'Option',
}: Props) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleAdd = useCallback(() => {
    const newItems = [...items, `${placeholder} ${items.length + 1}`];
    onChange(newItems);
    setEditingIndex(newItems.length - 1);
  }, [items, onChange, placeholder]);

  const handleRemove = useCallback((index: number) => {
    if (items.length <= minItems) return;
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
    if (editingIndex === index) setEditingIndex(null);
  }, [items, minItems, onChange, editingIndex]);

  const handleChange = useCallback((index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = value;
    onChange(newItems);
  }, [items, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Add new item after current
      const newItems = [...items];
      newItems.splice(index + 1, 0, `${placeholder} ${items.length + 1}`);
      onChange(newItems);
      setEditingIndex(index + 1);
    }
    if (e.key === 'Tab') {
      e.preventDefault();
      if (index < items.length - 1) {
        setEditingIndex(index + 1);
      } else {
        setEditingIndex(null);
      }
    }
    if (e.key === 'Escape') {
      setEditingIndex(null);
    }
  }, [items, onChange, placeholder]);

  // Drag-and-drop reorder
  const handleDragStart = useCallback((index: number) => {
    setDragIndex(index);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  }, []);

  const handleDrop = useCallback((index: number) => {
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const newItems = [...items];
    const [moved] = newItems.splice(dragIndex, 1);
    newItems.splice(index, 0, moved);
    onChange(newItems);
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, items, onChange]);

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-medium text-muted">{label}</label>
        <button
          onClick={handleAdd}
          className="p-0.5 rounded hover:bg-surface-2 text-muted hover:text-primary transition-colors"
          title={`Add ${placeholder}`}
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="space-y-0.5 bg-surface-2 rounded-lg border border-default overflow-hidden">
        {items.map((item, index) => (
          <div
            key={index}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={() => handleDrop(index)}
            onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
            className={`
              flex items-center gap-1 px-1 py-1 group
              ${dragOverIndex === index ? 'border-t-2 border-primary' : 'border-t border-transparent'}
              ${dragIndex === index ? 'opacity-40' : ''}
              ${index > 0 ? 'border-t border-default' : ''}
              hover:bg-surface-3/50 transition-colors
            `}
          >
            {/* Drag handle */}
            <div className="cursor-grab active:cursor-grabbing text-muted/40 hover:text-muted flex-shrink-0">
              <GripVertical className="w-3 h-3" />
            </div>

            {/* Text / Edit */}
            {editingIndex === index ? (
              <input
                ref={inputRef}
                type="text"
                value={item}
                onChange={(e) => handleChange(index, e.target.value)}
                onBlur={() => setEditingIndex(null)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                className="flex-1 bg-surface px-1.5 py-0.5 text-xs rounded outline-none border border-primary text-default min-w-0"
                autoFocus
              />
            ) : (
              <span
                onClick={() => setEditingIndex(index)}
                className="flex-1 text-xs text-default cursor-text truncate px-1.5 py-0.5"
              >
                {item || <span className="text-muted italic">Empty</span>}
              </span>
            )}

            {/* Remove */}
            {items.length > minItems && (
              <button
                onClick={() => handleRemove(index)}
                className="p-0.5 text-muted hover:text-danger opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                title="Remove"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});
